// Stage 5 C-A §11/§12/§13 — Files library + exact-version Preview + Previous Versions.
//
// The Files list is a projection over the shared File / File Version
// records. Preview binds to the EXACT selected version, not silent
// substitution to the current version.  Withdrawn versions surface
// as a proper Withdrawn state, not access errors (§17).
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { portalApi, fmtDate } from "./portalApi";
import {
    PageHeader, Card, CardHeader, Pill, Button, EmptyState, Spinner,
    AccessState, Breadcrumbs,
} from "./PortalUi";
import { usePageMeta } from "../../components/Layout";

function human(bytes) {
    if (!bytes && bytes !== 0) return "—";
    const u = ["B","KB","MB","GB"]; let i = 0; let n = bytes;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export function PortalFiles() {
    usePageMeta({ title: "Files | Studio G4 Portal" });
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");
    const [category, setCategory] = useState("");
    const [ws, setWs] = useState("");
    const [includePrev, setIncludePrev] = useState(false);
    const [selection, setSelection] = useState({});
    const [downloading, setDownloading] = useState(false);

    async function load() {
        try {
            const params = {};
            if (category) params.category = category;
            if (ws) params.workstage = parseInt(ws, 10);
            if (includePrev) params.include_previous = true;
            const r = await portalApi.files(params);
            setItems(r.data.items || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Unavailable");
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, ws, includePrev]);

    const categories = useMemo(() => {
        const s = new Set((items || []).map(i => i.category).filter(Boolean));
        return Array.from(s);
    }, [items]);
    const workstages = useMemo(() => {
        const s = new Set((items || []).map(i => i.originating_workstage).filter(v => v != null));
        return Array.from(s).sort();
    }, [items]);

    const anySelected = Object.values(selection).some(Boolean);

    async function downloadZip() {
        setDownloading(true);
        try {
            const ids = Object.keys(selection).filter(k => selection[k]);
            const resp = await portalApi.filesZip(ids);
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url; a.download = "sg4-files.zip"; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            alert("Download failed");
        } finally { setDownloading(false); }
    }
    async function downloadAll() {
        setDownloading(true);
        try {
            const ids = (items || []).map(i => i.id);
            const resp = await portalApi.filesZip(ids);
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url; a.download = "sg4-files-all.zip"; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            alert("Download failed");
        } finally { setDownloading(false); }
    }

    if (err) return <div className="py-8 px-4"><EmptyState heading="Files unavailable" note={err} testid="portal-files-err" /></div>;
    if (items === null) return <Spinner testid="portal-files-loading" />;

    return (
        <div className="py-8 px-4" data-testid="portal-files">
            <PageHeader kicker="Project library" title="Files"
                right={
                    <div className="flex items-center gap-2">
                        {anySelected && (
                            <Button variant="secondary" onClick={downloadZip} disabled={downloading} testid="portal-files-download-selected">
                                {downloading ? "Preparing…" : "Download selected"}
                            </Button>
                        )}
                        {items.length > 0 && (
                            <Button variant="primary" onClick={downloadAll} disabled={downloading} testid="portal-files-download-all">
                                Download all
                            </Button>
                        )}
                    </div>
                }
                testid="portal-files-header" />

            <Card className="mb-4" testid="portal-files-filters">
                <div className="p-4 flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-[12px]">
                        <span className="text-[#8A8A84] font-manrope font-semibold uppercase tracking-[0.08em] text-[10.5px]">Workstage</span>
                        <select value={ws} onChange={e => setWs(e.target.value)} data-testid="portal-files-filter-ws"
                            className="px-2 py-1 border border-[#D7D4CC] rounded text-[13px] bg-white">
                            <option value="">All</option>
                            {workstages.map(w => <option key={w} value={w}>WS{w}</option>)}
                        </select>
                    </label>
                    <label className="flex items-center gap-2 text-[12px]">
                        <span className="text-[#8A8A84] font-manrope font-semibold uppercase tracking-[0.08em] text-[10.5px]">Category</span>
                        <select value={category} onChange={e => setCategory(e.target.value)} data-testid="portal-files-filter-category"
                            className="px-2 py-1 border border-[#D7D4CC] rounded text-[13px] bg-white">
                            <option value="">All</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </label>
                    <label className="flex items-center gap-2 text-[12px] ml-auto">
                        <input type="checkbox" checked={includePrev} onChange={e => setIncludePrev(e.target.checked)} data-testid="portal-files-include-previous"
                            className="w-4 h-4" />
                        <span className="text-[#4A4A46] font-manrope">Include previous versions</span>
                    </label>
                </div>
            </Card>

            {!items.length ? (
                <EmptyState heading="No files yet"
                    note="Files issued to you by your project team will appear here."
                    testid="portal-files-empty" />
            ) : (
                <Card>
                    <table className="w-full">
                        <thead className="bg-[#FAF7F1]">
                            <tr className="text-[10.5px] uppercase tracking-[0.1em] text-[#8A8A84] font-manrope font-semibold">
                                <th className="text-left px-4 py-2 w-6">
                                    <input type="checkbox" data-testid="portal-files-select-all"
                                        onChange={e => {
                                            const checked = e.target.checked;
                                            const next = {};
                                            items.forEach(i => next[i.id] = checked);
                                            setSelection(next);
                                        }}
                                        checked={items.every(i => !!selection[i.id]) && items.length > 0}
                                    />
                                </th>
                                <th className="text-left px-3 py-2">Doc No.</th>
                                <th className="text-left px-3 py-2">File name</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-left px-3 py-2">Category</th>
                                <th className="text-left px-3 py-2">Workstage</th>
                                <th className="text-left px-3 py-2">Uploaded / Issued by</th>
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-left px-3 py-2">Version / State</th>
                                <th className="text-left px-3 py-2 w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(f => (
                                <tr key={f.id} className="border-t border-[#EEEBE4]" data-testid={`portal-files-row-${f.id}`}>
                                    <td className="px-4 py-2">
                                        <input type="checkbox" checked={!!selection[f.id]} onChange={e => setSelection(prev => ({ ...prev, [f.id]: e.target.checked }))} data-testid={`portal-files-select-${f.id}`} />
                                    </td>
                                    <td className="px-3 py-2 text-[12px] text-[#4A4A46] font-mono">{f.document_number || "—"}</td>
                                    <td className="px-3 py-2 text-[13.5px] text-charcoal font-manrope">{f.filename || "—"}</td>
                                    <td className="px-3 py-2 text-[12px] text-[#6B6B67]">{f.file_type || "—"}</td>
                                    <td className="px-3 py-2 text-[12px] text-[#6B6B67]">{f.category_label || "—"}</td>
                                    <td className="px-3 py-2 text-[12px] text-[#6B6B67]">{f.workstage_label || (f.originating_workstage ? `WS${f.originating_workstage}` : "—")}</td>
                                    <td className="px-3 py-2 text-[12px] text-[#6B6B67]">{f.uploaded_by_label || "—"}</td>
                                    <td className="px-3 py-2 text-[12px] text-[#6B6B67]">{fmtDate(f.created_at)}</td>
                                    <td className="px-3 py-2 text-[12px] text-[#6B6B67]">v{f.current_version_number || 1} · {f.version_state_label || "Current"}</td>
                                    <td className="px-3 py-2 text-right">
                                        <Link to={`/portal/files/${f.id}/preview`} className="text-[12px] text-[#A8543F] hover:underline font-manrope font-semibold" data-testid={`portal-files-view-${f.id}`}>View</Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}

// ---------- File Preview (§12 exact version) ----------

export function PortalFilePreview() {
    const { fileId } = useParams();
    const [params] = useSearchParams();
    const versionId = params.get("version_id") || undefined;
    const versionNumber = params.get("version_number") || undefined;
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const previewLabel = data?.filename || "File";
    usePageMeta({ title: `${previewLabel} · Files | Studio G4 Portal` });
    useEffect(() => {
        const qs = {};
        if (versionId) qs.version_id = versionId;
        if (versionNumber) qs.version_number = parseInt(versionNumber, 10);
        portalApi.filePreview(fileId, qs)
            .then(r => setData(r.data))
            .catch(e => {
                if (e?.response?.status === 404) setErr("not_found");
                else setErr(e?.response?.data?.detail || "Unavailable");
            });
    }, [fileId, versionId, versionNumber]);
    if (err === "not_found") return <div className="py-8 px-4"><AccessState kind="unavailable" note="The file or version you requested isn't available." testid="portal-file-preview-notfound" /></div>;
    if (err) return <div className="py-8 px-4"><AccessState kind="temporary" note={err} testid="portal-file-preview-err" /></div>;
    if (!data) return <Spinner testid="portal-file-preview-loading" />;

    if (data.state === "withdrawn") {
        return (
            <div className="py-8 px-4" data-testid="portal-file-preview-withdrawn">
                <Breadcrumbs items={[{ label: "Files", to: "/portal/files" }, { label: data.filename || fileId }]} />
                <PageHeader kicker="File preview" title={data.filename || fileId}
                    right={<Pill tone="warn">Withdrawn</Pill>} />
                <AccessState kind="withdrawn" note={data.message} testid="portal-file-preview-withdrawn-state" />
                <div className="text-center mt-4">
                    <Link to={`/portal/files/${fileId}/versions`}>
                        <Button variant="secondary" testid="portal-file-preview-view-history">View previous versions</Button>
                    </Link>
                </div>
            </div>
        );
    }
    return (
        <div className="py-8 px-4" data-testid="portal-file-preview">
            <Breadcrumbs items={[
                { label: "Files", to: "/portal/files" },
                { label: data.filename || fileId },
            ]} />
            <PageHeader kicker={`Version ${data.version_number || 1}`} title={data.filename || fileId}
                right={
                    <div className="flex items-center gap-2">
                        <Link to={`/portal/files/${fileId}/versions`}>
                            <Button variant="secondary" testid="portal-file-preview-versions">Previous versions</Button>
                        </Link>
                        <a href={`${process.env.REACT_APP_BACKEND_URL || ""}${data.download_url}`}>
                            <Button variant="primary" testid="portal-file-preview-download">Download</Button>
                        </a>
                    </div>
                } />
            <Card><div className="p-4">
                <div className="grid grid-cols-3 gap-4 text-[12px] mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Type</div>
                        <div className="text-charcoal">{data.content_type || "—"}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Size</div>
                        <div className="text-charcoal">{human(data.size)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Issued</div>
                        <div className="text-charcoal">{fmtDate(data.issued_at)}</div>
                    </div>
                </div>
                {data.preview_url ? (
                    (data.content_type || "").startsWith("image/") ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${data.preview_url}`}
                             alt={data.filename || "preview"}
                             data-testid="portal-file-preview-image"
                             className="w-full max-h-[600px] object-contain bg-[#FAF7F1] rounded" />
                    ) : (
                        <iframe src={`${process.env.REACT_APP_BACKEND_URL || ""}${data.preview_url}`}
                                title={data.filename || "preview"}
                                data-testid="portal-file-preview-iframe"
                                className="w-full h-[600px] bg-[#FAF7F1] rounded border border-[#EEEBE4]" />
                    )
                ) : (
                    <div className="bg-[#FAF7F1] border border-dashed border-[#DAD6CD] rounded p-8 text-center text-[13px] text-[#6B6B67]" data-testid="portal-file-preview-fallback">
                        Preview isn't supported for this file type. Please download to view offline.
                    </div>
                )}
            </div></Card>
        </div>
    );
}

// ---------- Previous Versions (§13) ----------

export function PortalFileVersions() {
    const { fileId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const versionsLabel = data?.filename || "File";
    usePageMeta({ title: `Versions · ${versionsLabel} | Studio G4 Portal` });
    useEffect(() => {
        portalApi.fileVersions(fileId)
            .then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, [fileId]);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Versions unavailable" note={err} testid="portal-file-versions-err" /></div>;
    if (!data) return <Spinner testid="portal-file-versions-loading" />;

    const versions = data.versions || [];
    return (
        <div className="py-8 px-4" data-testid="portal-file-versions">
            <Breadcrumbs items={[
                { label: "Files", to: "/portal/files" },
                { label: data.filename || fileId, to: `/portal/files/${fileId}/preview` },
                { label: "Previous versions" },
            ]} />
            <PageHeader kicker="Version history" title={data.filename || fileId} />
            {versions.length === 0 ? (
                <EmptyState heading="No previous versions" note="This file has only its current version." testid="portal-file-versions-empty" />
            ) : (
                <Card>
                    <div className="divide-y divide-[#EEEBE4]">
                        {versions.map(v => (
                            <div key={v.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`portal-file-version-${v.id}`}>
                                <div>
                                    <div className="font-manrope text-[13.5px] text-charcoal">Version {v.version_number}</div>
                                    <div className="text-[11.5px] text-[#8A8A84] mt-0.5">
                                        {v.state === "withdrawn" ? "Withdrawn " : "Issued "}
                                        {fmtDate(v.withdrawn_at || v.issued_at || v.created_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Pill tone={v.state === "current" ? "ok" : v.state === "withdrawn" ? "warn" : "neutral"}>{v.state}</Pill>
                                    <Link to={`/portal/files/${fileId}/preview?version_id=${v.id}`}>
                                        <Button variant="secondary" testid={`portal-file-version-view-${v.id}`}>View</Button>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}
